import { useState, useEffect, useRef } from 'react';

interface TintedBackgroundOptions {
  tintColor?: string;
  tintOpacity?: number;
  zoomFactor?: number; // 1.05 = 5% zoom on background
  enabled?: boolean;
}

export function useTintedBackground(options: TintedBackgroundOptions = {}) {
  const {
    tintColor = '#10b981', // green
    tintOpacity = 0.05, // Much lighter default
    zoomFactor = 1.01, // Less zoom
    enabled = true
  } = options;

  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 }); // percentages
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled || !elementRef.current) return;

    const element = elementRef.current;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = element.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setMousePosition({ x, y });
    };

    element.addEventListener('mousemove', handleMouseMove);
    return () => element.removeEventListener('mousemove', handleMouseMove);
  }, [enabled]);

  const backgroundStyle = enabled ? {
    backgroundColor: 'rgba(255, 255, 255, 0.1)', // Light default
    backdropFilter: 'blur(0.5px) saturate(1.05) contrast(1.02)', // Minimal blur
    position: 'relative' as const,
    overflow: 'hidden' as const,
  } : {};

  const overlayStyle = enabled ? {
    background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, ${tintColor}${Math.round(tintOpacity * 255).toString(16)} 0%, transparent 60%)`,
    transform: `scale(${zoomFactor})`,
    transformOrigin: `${mousePosition.x}% ${mousePosition.y}%`,
    transition: 'transform 0.1s ease-out',
  } : {};

  return {
    ref: elementRef,
    style: backgroundStyle,
    overlayStyle,
    mousePosition
  };
}
